import type { ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { BlockConfirmTarget } from './gatewayControlUtils';
import { formatList } from './endpointEnforcement';

interface BlockConfirmationDialogProps {
  target: BlockConfirmTarget | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Bespoke block-confirmation modal (KIR-3940 AC 6). NOT `ConfirmationDialog`
 * (`@/components/ui/confirmation-dialog`) — that component's `description`
 * prop is a plain string, and the body here needs a tool-count + timing note
 * (server) or just a name (tool). No user-affected count (D2, sub-ticket).
 *
 * Also confirms blocking an Inventory server discovered on endpoints
 * (KIR-4482, `endpointServer`): same title, buttons and layout, with a body
 * that says where the endpoint hook enforces the block and where it does not.
 */
export function BlockConfirmationDialog({
  target,
  onCancel,
  onConfirm,
}: BlockConfirmationDialogProps): ReactElement {
  const isOpen = target !== null;
  const name =
    target?.kind === 'tool' ? target.toolName : (target?.serverName ?? '');

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Block {name}?</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-muted-foreground space-y-2">
          {target?.kind === 'endpointServer' ? (
            <EndpointServerBody target={target} />
          ) : (
            <>
              {target?.kind === 'server' ? (
                <p>
                  This blocks <strong>{target.serverName}</strong> and all its{' '}
                  {target.toolCount} tool{target.toolCount === 1 ? '' : 's'}.
                </p>
              ) : (
                <p>
                  This blocks the <strong>{target?.toolName}</strong> tool.
                </p>
              )}
              <p>The gateway picks up this change within ~10 seconds.</p>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            Block
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EndpointServerBody({
  target,
}: {
  target: Extract<BlockConfirmTarget, { kind: 'endpointServer' }>;
}): ReactElement {
  const users = target.userCount;
  return (
    <>
      <p>
        Kirin&apos;s endpoint hook denies <strong>{target.serverName}</strong>
        &apos;s tool calls in {formatList(target.enforcedAssistants)}.
        {target.notEnforcedAssistants.length > 0 && (
          <>
            {' '}
            It is not blocked in {formatList(target.notEnforcedAssistants)},
            which Kirin does not enforce MCP blocks in.
          </>
        )}
      </p>
      <p>
        The block follows the server itself (its package or URL), so renaming it
        does not lift the block. A server Kirin could not identify is matched by
        name, ignoring case and punctuation.
      </p>
      <p>
        {users} user{users === 1 ? ' has' : 's have'} this server configured.
        Security scanning keeps running, so its detections still appear in
        Alerts. Endpoints pick up this change within ~30 seconds.
      </p>
    </>
  );
}
